import type { FileRecord } from "@cohesive/api-client";

// Default size for non-image/video file_record canvas tiles. Width matches the
// generic tile size; height approximates a 2:1 thumbnail (~175px at 350 wide)
// plus the ~46px embedded footer (px-2.5 py-2 + a 12px title line + 10px meta
// line + 4px gap). Pinned to 200 (a multiple of 50) rather than ~210 so the
// node's center sits on the 25px position grid, letting edges between these and
// other fixed-size nodes (text notes, shapes) be drawn exactly straight.
//
// Image/video tiles override this with their natural aspect via
// `fitImageNodeSize` (and are inherently off-grid — see the edge-alignment
// analysis).
export const DEFAULT_FILE_NODE_WIDTH = 350;
export const DEFAULT_FILE_NODE_HEIGHT = 200;

/**
 * Minimal file_record entity shape per `base-file-record.json`, plus
 * a few optional hints we seed at upload time so the canvas can render
 * the correct aspect/icon before hydration completes:
 *
 * - `content_type` is required by `getEntityMediaDisplayBox` to decide
 *   whether the entity is image/video (and therefore should keep its
 *   computed aspect instead of falling back to `aspect-square`).
 * - `width`/`height` are read by the same helper to compute the fitted
 *   media box without waiting on the server's `metadata.width/height`
 *   (which only appears post-processing).
 *
 * These optional fields are intentionally compatible with the wire
 * spread done by `useHydrateFileRecordEntity` — the server's `FileRecord`
 * doesn't carry top-level `width`/`height`, so our pre-seeded values
 * survive hydration unchanged.
 */
export interface FileRecordCanvasEntity {
  type: "file_record";
  base: "file_record";
  platform: "cohesive";
  data: {
    id: string;
    title: string;
    content_type?: string;
    width?: number;
    height?: number;
  };
}

export type FileRecordCanvasMetadata = {
  /** Authoritative server record for id + path (title derives from path leaf). */
  record: FileRecord;
  /** Client-measured natural width — used for initial node sizing when the
   *  server hasn't finished ingest yet. */
  naturalWidth?: number;
  /** Client-measured natural height. */
  naturalHeight?: number;
  /** MIME from the upload pipeline (post-HEIC). Falls back to record.content_type. */
  contentType?: string;
};

/**
 * Build a file_record canvas entity + initial node dimensions from upload
 * metadata. `entity.data` carries minimum-viable hints (id, title,
 * content_type, optional dims) so the canvas card renders the correct
 * aspect / icon before `useHydrateFileRecordEntity` finishes; hydration
 * spreads the full FileRecord on top later, but doesn't clobber these
 * fields because they don't exist at the wire's top level.
 */
export function buildFileRecordEntityForCanvas(
  meta: FileRecordCanvasMetadata,
): {
  entity: FileRecordCanvasEntity;
  nodeWidth: number;
  nodeHeight: number;
} {
  const { record, naturalWidth, naturalHeight, contentType } = meta;
  const ct = contentType || record.content_type || "";
  const title = record.path.split("/").pop() || record.id;

  // Prefer client-measured natural dims (upload path), fall back to the
  // server-stored dims on the record itself (copy/place-existing paths).
  const w = naturalWidth ?? record.width;
  const h = naturalHeight ?? record.height;

  const isImage =
    ct.startsWith("image/") && w != null && h != null && w > 0 && h > 0;
  const box = isImage
    ? fitImageNodeSize(w, h)
    : { width: DEFAULT_FILE_NODE_WIDTH, height: DEFAULT_FILE_NODE_HEIGHT };

  return {
    entity: {
      type: "file_record",
      base: "file_record",
      platform: "cohesive",
      data: {
        id: record.id,
        title,
        ...(ct ? { content_type: ct } : {}),
        ...(isImage ? { width: w, height: h } : {}),
      },
    },
    nodeWidth: box.width,
    nodeHeight: box.height,
  };
}

export { readImageFileDimensions } from "@/utils/imageFilePrep";

export type ImageNodeSize = { width: number; height: number };

/**
 * Fit a node box so the longer side equals maxEdge (default 350), preserving aspect ratio.
 * If the shorter side would be below minShortEdge (default 8), scale up uniformly so the
 * short side is minShortEdge, then if the long side exceeds maxEdge, scale down uniformly
 * to cap the long side at maxEdge (short side may end up slightly above minShortEdge).
 */
export function fitImageNodeSize(
  naturalW: number,
  naturalH: number,
  maxEdge = 350,
  minShortEdge = 8,
): ImageNodeSize {
  if (naturalW <= 0 || naturalH <= 0) {
    return { width: maxEdge, height: maxEdge };
  }
  const longSide = Math.max(naturalW, naturalH);
  let w = (naturalW / longSide) * maxEdge;
  let h = (naturalH / longSide) * maxEdge;
  const short = Math.min(w, h);
  if (short < minShortEdge && short > 0) {
    const bump = minShortEdge / short;
    w *= bump;
    h *= bump;
  }
  const longAfter = Math.max(w, h);
  if (longAfter > maxEdge && longAfter > 0) {
    const cap = maxEdge / longAfter;
    w *= cap;
    h *= cap;
  }
  return { width: w, height: h };
}

/**
 * Fitted node size for entities that carry pixel dimensions in `data`.
 * Covers: generated `base: "file"` with image mime and `base: "file_record"`.
 */
export function getEntityMediaDisplayBox(
  entity: unknown,
): ImageNodeSize | null {
  if (!entity || typeof entity !== "object") {
    return null;
  }
  const e = entity as {
    type?: string;
    base?: string;
    data?: Record<string, unknown>;
  };
  if (!e.data || typeof e.data !== "object") {
    return null;
  }
  const mw = e.data.width;
  const mh = e.data.height;
  if (typeof mw !== "number" || typeof mh !== "number" || mw <= 0 || mh <= 0) {
    return null;
  }
  if (e.base === "file") {
    const mime = String(e.data.mime_type ?? "");
    if (mime.startsWith("image/")) {
      return fitImageNodeSize(mw, mh);
    }
  }
  if (e.base === "file_record") {
    const mime = String(
      e.data.content_type ?? (e.data as { mime_type?: string }).mime_type ?? "",
    );
    if (mime.startsWith("image/") || mime.startsWith("video/")) {
      return fitImageNodeSize(mw, mh);
    }
  }
  return null;
}

/**
 * Same as `getEntityMediaDisplayBox` but also looks at the
 * `metadata.width/height` shape that hydrated `file_record` entities
 * inherit from the server `FileRecord`. The server only populates
 * `metadata.width/height` once the processing pipeline finishes, so this
 * is the post-processing path; the top-level `e.data.width/height` path
 * (used by canvas drops) is the pre-processing one.
 */
export function getFileRecordMediaDisplayBoxFromMetadata(
  entity: unknown,
): ImageNodeSize | null {
  if (!entity || typeof entity !== "object") return null;
  const e = entity as {
    base?: string;
    data?: Record<string, unknown> & {
      metadata?: { width?: unknown; height?: unknown };
    };
  };
  if (e.base !== "file_record" || !e.data) return null;
  const mime = String(
    e.data.content_type ?? (e.data as { mime_type?: string }).mime_type ?? "",
  );
  if (!mime.startsWith("image/") && !mime.startsWith("video/")) return null;
  const mw = e.data.metadata?.width;
  const mh = e.data.metadata?.height;
  if (typeof mw !== "number" || typeof mh !== "number" || mw <= 0 || mh <= 0) {
    return null;
  }
  return fitImageNodeSize(mw, mh);
}
